/** Frontend domains; the existing backend discriminator is isCustomerCareChat. */
export type ConversationType = "customer_vendor" | "customer_care";
export interface MarketplaceAccount { role: "user" | "vendor"; id: string }
export interface ConversationParticipant {
  id: string;
  role: "user" | "vendor" | "customer_care" | "unknown";
  model: string;
  details: Record<string, unknown>;
  name: string | null;
  avatar: string | null;
  online: boolean | undefined;
}
export interface MarketplaceConversation {
  _id: string;
  type: "customer_vendor";
  participants: ConversationParticipant[];
  counterpart: ConversationParticipant;
  raw: Record<string, unknown>;
}

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
const string = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;
const identity = (value: unknown): string | null => {
  const data = record(value);
  return string(value) || string(data._id) || string(data.id);
};
const boolean = (...values: unknown[]): boolean | undefined => values.find(value => typeof value === "boolean") as boolean | undefined;

export const getConversationId = (value: unknown): string | null => {
  const data = record(value);
  return identity(value) || identity(data.chatId);
};
export const getPayloadChatId = (value: unknown): string | null => {
  const data = record(value);
  return identity(data.chatId) || identity(data.chat);
};
export const getPayloadSenderId = (value: unknown): string | null => {
  const data = record(value);
  return identity(data.sender) || identity(data.senderId) || identity(data.from);
};

const roleFromModel = (model: string): ConversationParticipant["role"] => {
  if (/^(users?|customers?)$/i.test(model)) return "user";
  if (/^vendors?$/i.test(model)) return "vendor";
  if (/^(admins?|whatsadmins?|customer[_ -]?care|support)$/i.test(model)) return "customer_care";
  return "unknown";
};

export const readConversationParticipants = (value: unknown): ConversationParticipant[] => {
  const participants = record(value).participants;
  if (!Array.isArray(participants)) return [];
  return participants.map(raw => {
    const participant = record(raw);
    const populated = record(participant.participantId);
    const details = { ...participant, ...populated, ...record(participant.details) };
    const model = string(participant.model) || string(populated.model) || "";
    const explicitRole = roleFromModel(model);
    const role = explicitRole !== "unknown" ? explicitRole
      : typeof participant.isVendor === "boolean" ? participant.isVendor ? "vendor" : "user" : "unknown";
    return {
      id: (Object.prototype.hasOwnProperty.call(participant, "participantId")
        ? identity(participant.participantId) : identity(raw)) || "",
      role, model, details,
      name: string(details.storeName) || string(details.name),
      avatar: string(details.businessLogo) || string(details.avatar) || string(details.profileImage),
      online: boolean(participant.isOnline, details.isOnline, details.online),
    };
  });
};

const supportIds = new Map<string, Set<string>>();
const accountKey = (account: MarketplaceAccount) => account.role + ":" + account.id;
const supportStorageKey = (account: MarketplaceAccount) => "mbaay:customer-care:" + accountKey(account) + ":thread-ids";
const storage = (): Storage | null => { try { return typeof localStorage === "undefined" ? null : localStorage; } catch { return null; } };

const knownSupportIds = (account: MarketplaceAccount): Set<string> => {
  const key = accountKey(account);
  const ids = supportIds.get(key) || new Set<string>();
  supportIds.set(key, ids);
  try {
    const browserStorage = storage();
    try {
      const cached: unknown = JSON.parse(browserStorage?.getItem(supportStorageKey(account)) || "[]");
      if (Array.isArray(cached)) for (const id of cached) { const parsed = string(id); if (parsed) ids.add(parsed); }
    } catch { /* A corrupt registry must not prevent reading the current support ID. */ }
    const current = browserStorage?.getItem("mbaay:customer-care:" + key + ":chatId");
    if (string(current)) ids.add(current!);
  } catch { /* Backend role/discriminator checks still apply when storage is unavailable. */ }
  return ids;
};

/** Called only after an admin-specific start/history request establishes provenance. */
export const registerCustomerCareConversation = (account: MarketplaceAccount, chatId: string): void => {
  if (!account.id || !string(chatId)) return;
  const ids = knownSupportIds(account);
  if (ids.has(chatId)) return;
  ids.add(chatId);
  try { storage()?.setItem(supportStorageKey(account), JSON.stringify([...ids])); } catch { /* Memory registry remains isolated. */ }
  if (typeof window !== "undefined") window.dispatchEvent(new Event("mbaay:conversation-boundary-change"));
};

export const isKnownCustomerCareConversation = (account: MarketplaceAccount, chatId: string): boolean =>
  Boolean(chatId && knownSupportIds(account).has(chatId));

export const classifyConversation = (
  value: unknown,
  account?: MarketplaceAccount,
): ConversationType | "unclassified" => {
  const data = record(value);
  const id = getConversationId(value);
  const participants = readConversationParticipants(value);
  if (data.isCustomerCareChat === true || (account && id && isKnownCustomerCareConversation(account, id)) ||
      participants.some(participant => participant.role === "customer_care")) return "customer_care";
  if (!id || participants.length !== 2 || participants.some(participant => !participant.id) ||
      participants[0].id === participants[1].id) return "unclassified";
  if (!participants.some(participant => participant.role === "user") ||
      !participants.some(participant => participant.role === "vendor")) return "unclassified";
  if (account && !participants.some(participant => participant.id === account.id && participant.role === account.role)) return "unclassified";
  return "customer_vendor";
};

export const readMarketplaceConversation = (value: unknown, account: MarketplaceAccount): MarketplaceConversation | null => {
  if (!account.id || classifyConversation(value, account) !== "customer_vendor") return null;
  const participants = readConversationParticipants(value);
  const counterpart = participants.find(participant => participant.id !== account.id)!;
  return { _id: getConversationId(value)!, type: "customer_vendor", participants, counterpart, raw: record(value) };
};
